import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound } from 'lucide-react';
import VaultPassphraseChangeModal from './VaultPassphraseChangeModal';
import { isTauri } from '../utils/tauri';
import {
  useVaultStore,
  useVaultMasterStore,
  openVaultGate,
  refreshVaultMaster,
} from '../store/vaultStore';
import './Vault.css';

/**
 * The vault sidebar's master-passphrase block (CONTRACT §6.4 entry point and
 * §6.6), sitting above `Browser integration`.
 *
 * The OS-keychain "unlock automatically on this computer" cache is gone as of
 * vault-storage-transition phase 6 (D-A: no keychain of any kind, session-only
 * unlock) — `vault_master_remember` no longer exists Rust-side. This block
 * used to offer that toggle; the control (and the hazard-H3 "off requires
 * typing the passphrase" guard that protected it) is deleted along with it,
 * not merely hidden. `SHOW_REMEMBER_TOGGLE` stays exported, always `false`,
 * only because `vaultPassphraseChange.test.js` still imports it to gate its
 * (already-skipped) toggle tests — flipping it back on has no effect and
 * cannot: nothing here reads it as a switch for anything anymore.
 */
export const SHOW_REMEMBER_TOGGLE = false;

// `variant="settings"` renders as a standard Settings row (label + desc on the
// left, action button on the right) for the Settings → Extensions → Vault page;
// the default keeps the original sidebar-style block. Both share all handlers
// and the two modals below.
export default function VaultPassphraseSection({ variant = 'sidebar' }) {
  const { t } = useTranslation();
  const master = useVaultMasterStore((s) => s.master);
  const status = useVaultStore((s) => s.status);
  const [changeOpen, setChangeOpen] = useState(false);

  const reload = useCallback(() => {
    refreshVaultMaster();
  }, []);

  useEffect(() => {
    if (!isTauri()) return undefined;
    reload();
    const onChanged = () => reload();
    window.addEventListener('vault-master-changed', onChanged);
    return () => window.removeEventListener('vault-master-changed', onChanged);
  }, [reload]);

  if (!isTauri()) return null;

  // Never offer to set a passphrase on a vault whose key state is broken: the
  // mode reads `keyringOnly` there, but the file cannot be locked to anything
  // and Rust refuses. `VaultPassphraseBanner` shows the recovery line instead.
  if (master?.keyMissing) return null;

  const isProtected = master ? master.mode === 'protected' : !!status?.protected;
  const remembered = master ? !!master.remembered : !!status?.remembered;
  const itemCount = Number(status?.itemCount) || 0;

  if (variant === 'settings') {
    return (
      <>
        <div className="settings-row">
          <div className="min-w-0">
            <div className="settings-row-label">
              {t('vault.passphraseHeading', 'Vault passphrase')}
              {isProtected && remembered && (
                <span className="vault-bridge-badge" style={{ marginLeft: 8 }}>
                  {t('vault.rememberBadge', 'auto-unlock on')}
                </span>
              )}
            </div>
            <div className="settings-row-desc">
              {isProtected
                ? t(
                    'vault.passphraseOnHint',
                    'This vault opens with your passphrase, so it can be opened on any computer.'
                  )
                : t(
                    'vault.passphraseNoneHint',
                    'This vault can only be opened on this computer. A passphrase lets you open it anywhere.'
                  )}
            </div>
          </div>
          <div className="flex items-center flex-shrink-0 ml-4">
            <button
              type="button"
              className="settings-action-btn"
              onClick={isProtected ? () => setChangeOpen(true) : () => openVaultGate('migrate')}
            >
              <KeyRound size={13} />
              {isProtected
                ? t('vault.changePrimary', 'Change passphrase')
                : t('vault.migrateBannerPrimary', 'Set a passphrase')}
            </button>
          </div>
        </div>
        {renderModals()}
      </>
    );
  }

  return (
    <>
      <div className="vault-bridge-head" style={{ marginTop: 12 }}>
        <span className="vault-bridge-title">
          {t('vault.passphraseHeading', 'Vault passphrase')}
        </span>
        {isProtected && remembered && (
          <span className="vault-bridge-badge">
            {t('vault.rememberBadge', 'auto-unlock on')}
          </span>
        )}
      </div>

      {!isProtected ? (
        <>
          <div className="vault-passkey-hint" style={{ marginLeft: 0 }}>
            {t(
              'vault.passphraseNoneHint',
              'This vault can only be opened on this computer. A passphrase lets you open it anywhere.'
            )}
          </div>
          <button
            type="button"
            className="vault-pass-secondary-btn"
            onClick={() => openVaultGate('migrate')}
          >
            <KeyRound size={13} />
            {t('vault.migrateBannerPrimary', 'Set a passphrase')}
          </button>
        </>
      ) : (
        <>
          {/* The OS-keychain "unlock automatically" toggle lived here — deleted
              along with `vault_master_remember`, not just hidden behind
              SHOW_REMEMBER_TOGGLE (vault-storage-transition phase 6, D-A: no
              keychain, session-only unlock). */}
          <button
            type="button"
            className="vault-pass-secondary-btn"
            onClick={() => setChangeOpen(true)}
          >
            <KeyRound size={13} />
            {t('vault.changePrimary', 'Change passphrase')}
          </button>
        </>
      )}

      {renderModals()}
    </>
  );

  // Shared by both variants: the change-passphrase modal.
  function renderModals() {
    return (
      <VaultPassphraseChangeModal
        isOpen={changeOpen}
        remembered={remembered}
        itemCount={itemCount}
        onClose={() => {
          setChangeOpen(false);
          reload();
        }}
      />
    );
  }
}
